<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";
import { isAxiosError } from "axios";
import { format as formatDate } from "date-fns";
import { Copy, Loader2 } from "@lucide/vue";

import { useModalStore } from "@/stores/modals/modals.store";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";
import TagItem from "@/components/Reusables/Utils/TagItem.vue";
import { insertProjectClone } from "@/api/services/ProjectRevision";
import SpeckleStreamChoice from "./components/SpeckleStreamChoice.vue";
import {
  CLONE_PREFERRED_MODE,
  NEW_STREAM_SELECTION,
  offersSpeckleStreamChoice,
  type SpeckleStreamSelection,
} from "./components/speckleStreamChoice";
import { useProjectVersions } from "../composables/useProjectVersions.composable";
import { PROJECT_NAME_TAKEN_MESSAGE, useProjectNameAvailability } from "@/composables/useProjectNameAvailability.composable";
import type { ProjectVersion } from "../types/projectVersion.types";

// Clone-the-current-state confirmation, the counterpart of RestoreProjectVersionModal: the same
// server-side mechanism, pinned at the live head instead of a trail row. Materials, project
// information, mappings, custom emission factors, team and the full history all travel into a brand
// new project; the source project is untouched. Admins only (the menus gate the row, the endpoint
// enforces it).
//
// There is deliberately nothing to pick here and no version number in the copy: the clone always
// pins to the project as it is right now, so the source row below is shown purely so the user can
// see WHICH state that is (RTLCA-5287).
const props = defineProps<{
  projectId: string;
  /**
   * The project's LIVE name — not `displayProjectNameFor`. The clone pins to the working state, so
   * naming the copy after a previewed frozen revision would describe it wrongly.
   */
  projectName: string;
  /**
   * The SOURCE project's Speckle project id — `speckleStreamId` in Hasura, which kept the old word
   * for what Speckle now calls a project. Decides whether the Speckle choice is offered at all: a
   * project with no Speckle project has nothing to share and nothing to replace, so the API ignores
   * the choice either way (RTLCA-5456).
   */
  speckleStreamId?: string;
}>();

const router = useRouter();
const toast = useToast();
const { closeByName } = useModalStore();

const { sortedVersions, loading, load } = useProjectVersions();

const newProjectName = ref(`${props.projectName} (Clone)`);
const isCloning = ref(false);

/**
 * The same tenant-wide duplicate-name check the create/edit wizard runs (RTLCA-5603). No exempt name:
 * `X (Clone)` is a name the system guessed rather than one this dialog owns, and cloning the same
 * project twice is precisely how a duplicate gets made — the backend accepts it silently.
 */
const { nameExists: nameTaken, check: checkName, checkImmediately: checkNameNow } = useProjectNameAvailability();

const nameError = computed(() => {
  if (!newProjectName.value.trim()) return "Give the new project a name";
  return nameTaken.value ? PROJECT_NAME_TAKEN_MESSAGE : "";
});

// The Speckle choice never blocks the confirm: both options are always answerable — one creates a
// project, the other keeps the one this project already has — so nameError stays the only guard.
const streamSelection = ref<SpeckleStreamSelection>(NEW_STREAM_SELECTION);
const offersStreamChoice = computed(() => offersSpeckleStreamChoice(props.speckleStreamId));

/**
 * The row the history page badges "Current": the newest row that is a version. Informational rows —
 * team changes, report exports — take no version number and hold no project state, so the newest of
 * those is never the state being cloned (RTLCA-5262).
 *
 * Null is a normal outcome, not just a failure: the trail is empty on a brand-new project, and the
 * versioning schema has not reached every environment yet (../ROLLOUT.md). Cloning works regardless
 * — it reads the live project, not the trail — so a missing row only costs the description below.
 */
const currentVersion = computed<ProjectVersion | null>(() => sortedVersions.value.find((row) => !row.informational) ?? null);

const currentVersionDate = computed(() =>
  currentVersion.value ? formatDate(new Date(currentVersion.value.createdAt), "MMM d, yyyy 'at' HH:mm") : ""
);

// Read the trail for that one row. Failures are silent on purpose: the store already reports them to
// Sentry, and this dialog has no reason to refuse a clone because it could not describe the source.
onMounted(() => {
  void load(props.projectId, props.projectName);

  // The seeded name is the one most likely to collide — a second clone of the same project proposes
  // `X (Clone)` again — and a user who accepts it without touching the field would never trigger an
  // input-driven check.
  checkNameNow(newProjectName.value.trim());
});

const cancel = () => closeByName("CloneProjectStateModal", { action: "cancel" });

// The API rejects a Speckle choice it cannot honour with a 400 whose body carries the reason —
// reachable from here only on "Create new project", which needs a linked Speckle account to create
// one with. Worth reading; anything else gets the generic line, the way the create wizard does it.
const cloneErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const message: unknown = error.response?.data?.message;
    if (typeof message === "string" && message.length > 0) return message;
  }
  return "Could not clone project. Please try again.";
};

const clone = async () => {
  // nameError now covers the duplicate name as well as the empty one, so this guard closes the gap
  // between a check landing and the button re-rendering — a fast click cannot beat it.
  if (nameError.value || isCloning.value) return;
  isCloning.value = true;
  const name = newProjectName.value.trim();
  const { mode, speckleStreamId } = streamSelection.value;
  try {
    const newProjectId = await insertProjectClone(props.projectId, name, mode, speckleStreamId);
    toast.success(`Created “${name}” from the current project state`);
    closeByName("CloneProjectStateModal", { action: "cloned", newProjectId });
    void router.push(`/project/${newProjectId}`);
  } catch (e) {
    Sentry.captureException(e);
    toast.error(cloneErrorMessage(e));
  } finally {
    isCloning.value = false;
  }
};

const btnBase =
  "focus-ring interactive-active t-small-bold duration-fast ease-standard inline-flex items-center justify-center gap-2 rounded-8 px-4 py-2 transition-colors";
</script>

<template>
  <div class="flex flex-col gap-4">
    <h2 class="t-sh4 text-type-default">Clone current project state to new project</h2>

    <p class="t-small text-type-muted">
      This creates a <span class="t-small-bold text-type-default">new project</span> with the materials and project information
      exactly as they are right now, along with the transport and waste mappings, custom emission factors, team, and the history
      up to this point. The current project is not changed.
    </p>

    <!-- What you're cloning. The "Current" tag sits in the right rail, the way it does on the
         history page (VersionRow.vue) — the same fact should not switch sides between the two. -->
    <div
      class="rounded-8 p-px-12 gap-px-12 flex items-start bg-slate-100 ring-1 ring-slate-200"
      data-cy="clone-project-source">
      <div class="gap-px-4 flex min-w-0 flex-1 flex-col">
        <!-- text-slate-300 colours the spinner's track (it inherits currentColor); the arc is the
             component's own app-blue fill. -->
        <template v-if="loading">
          <LoadingSpinner
            size="sm"
            fill="button"
            label="Loading the current project state"
            class="py-px-4 text-slate-300" />
        </template>
        <template v-else-if="currentVersion">
          <p class="t-small-bold text-type-default truncate">
            {{ currentVersion.summary }}<span v-if="currentVersion.entityLabel"> — {{ currentVersion.entityLabel }}</span>
          </p>
          <p class="t-caption text-type-muted">{{ currentVersionDate }} · {{ currentVersion.createdBy.name }}</p>
        </template>
        <template v-else>
          <p class="t-small-bold text-type-default truncate">{{ projectName }}</p>
          <p class="t-caption text-type-muted">The project exactly as it stands right now</p>
        </template>
      </div>
      <TagItem
        label="Current"
        color="primary" />
    </div>

    <!-- Trimmed on the way into the check because trimmed is what `clone()` submits, and the API
         compares the name with `_eq` — no trim, no case folding — so the two have to agree. -->
    <TextInputNew
      v-model="newProjectName"
      label="New project name"
      :error-msg="nameError"
      :disabled="isCloning"
      required
      @update:model-value="(value: string) => checkName(value.trim())" />

    <!-- Which Speckle project the clone connects to. Only offered when the source project has one —
         see offersSpeckleStreamChoice; the second test narrows the id to a string.
         Cloning leads with "Create new project": a clone is a new line of work, so a Speckle project
         of its own is the expected answer here — and the passed mode makes that explicit rather than
         leaning on the component's default. -->
    <SpeckleStreamChoice
      v-if="offersStreamChoice && speckleStreamId"
      :source-speckle-stream-id="speckleStreamId"
      :preferred-mode="CLONE_PREFERRED_MODE"
      :disabled="isCloning"
      @update:selection="streamSelection = $event" />

    <div class="gap-px-8 flex justify-end">
      <button
        type="button"
        :disabled="isCloning"
        :class="[
          btnBase,
          'bg-slate-0 text-type-default border border-slate-200 hover:bg-slate-100',
          { 'interactive-disabled': isCloning },
        ]"
        data-cy="clone-project-cancel"
        @click="cancel">
        Cancel
      </button>
      <button
        type="button"
        :disabled="!!nameError || isCloning"
        :class="[
          btnBase,
          !nameError && !isCloning
            ? 'bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover text-white'
            : 'text-type-muted bg-slate-200',
          { 'interactive-disabled': !!nameError || isCloning },
        ]"
        data-cy="clone-project-confirm"
        @click="clone">
        <Loader2
          v-if="isCloning"
          class="icon-sm animate-spin"
          :stroke-width="1.5" />
        <Copy
          v-else
          class="icon-sm"
          :stroke-width="1.5" />
        {{ isCloning ? "Cloning…" : "Clone Project" }}
      </button>
    </div>
  </div>
</template>
